import { useEffect, useState } from "react";
import api from "./api";
import {
  setCache,
  getCache,
  putReport,
  getReportLocal,
  allReports,
  deleteReportLocal,
} from "./db";

// ---- global sync state ----
let listeners = [];
let state = {
  online: typeof navigator !== "undefined" ? navigator.onLine : true,
  syncing: false,
  pending: 0,
  lastError: null,
};

function emit() {
  listeners.forEach((l) => l(state));
}
function setState(patch) {
  state = { ...state, ...patch };
  emit();
}

export function useSyncState() {
  const [s, setS] = useState(state);
  useEffect(() => {
    const l = (ns) => setS(ns);
    listeners.push(l);
    l(state);
    return () => {
      listeners = listeners.filter((x) => x !== l);
    };
  }, []);
  return s;
}

function isOffline(err) {
  return !navigator.onLine || err.code === "ERR_NETWORK" || !err.response;
}

// ---- cached collections (read-through) ----
async function fetchCached(cacheKey, url) {
  try {
    const { data } = await api.get(url);
    await setCache(cacheKey, data);
    if (!state.online) setState({ online: true });
    return data;
  } catch (err) {
    if (isOffline(err)) {
      setState({ online: false });
      const cached = await getCache(cacheKey);
      if (cached != null) return cached;
    }
    throw err;
  }
}

export const getCustomers = () => fetchCached("customers", "/customers");
export const getSites = (customerId) =>
  fetchCached(
    customerId ? `sites:${customerId}` : "sites",
    customerId ? `/sites?customer_id=${customerId}` : "/sites"
  );
export const getHeatPumps = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  const key = qs ? `heatpumps:${qs}` : "heatpumps";
  return fetchCached(key, qs ? `/heatpumps?${qs}` : "/heatpumps");
};
export const getDashboard = () => fetchCached("dashboard", "/dashboard");

// ---- reports (offline-first queue) ----
export async function loadReports(filter = {}) {
  const unfiltered = !filter.heat_pump_id && !filter.customer_id;
  try {
    const qs = new URLSearchParams(filter).toString();
    const { data } = await api.get(qs ? `/reports?${qs}` : "/reports");
    const serverIds = new Set(data.map((r) => r.id));
    for (const r of data) {
      const local = await getReportLocal(r.id);
      // server wins only if local already synced (don't clobber unsynced edits)
      if (!local || local._sync === "synced") {
        await putReport({ ...r, _sync: "synced" });
      }
    }
    // prune synced local reports that no longer exist on the server (e.g. deleted)
    if (unfiltered) {
      for (const r of await allReports()) {
        if (r._sync === "synced" && !serverIds.has(r.id)) {
          await deleteReportLocal(r.id);
        }
      }
    }
    if (!state.online) setState({ online: true });
  } catch (err) {
    if (isOffline(err)) setState({ online: false });
  }
  let rows = await allReports();
  if (filter.heat_pump_id)
    rows = rows.filter((r) => r.heat_pump_id === filter.heat_pump_id);
  if (filter.customer_id)
    rows = rows.filter((r) => r.customer_id === filter.customer_id);
  if (filter.status) rows = rows.filter((r) => r.status === filter.status);
  return rows;
}

export async function loadReport(id) {
  const local = await getReportLocal(id);
  if (local && local._sync !== "synced") return local;
  try {
    const { data } = await api.get(`/reports/${id}`);
    await putReport({ ...data, _sync: "synced" });
    return await getReportLocal(id);
  } catch (err) {
    if (local) return local;
    throw err;
  }
}

export async function saveReport(report) {
  const rec = {
    ...report,
    _sync: "local",
    updated_at: new Date().toISOString(),
  };
  await putReport(rec);
  syncNow();
  return rec;
}

export async function removeReport(id) {
  await deleteReportLocal(id);
  try {
    await api.delete(`/reports/${id}`);
  } catch (e) {
    /* ignore offline */
  }
}

export async function syncNow() {
  if (!navigator.onLine) {
    setState({ online: false });
    return;
  }
  const all = await allReports();
  const pending = all.filter(
    (r) => r._sync === "local" || r._sync === "error"
  );
  if (pending.length === 0) {
    setState({ pending: 0, online: true });
    return;
  }
  setState({ syncing: true, pending: pending.length, online: true });
  for (const r of pending) {
    try {
      await putReport({ ...r, _sync: "syncing" });
      const { _sync, ...payload } = r;
      await api.put(`/reports/${r.id}`, payload);
      const fresh = await getReportLocal(r.id);
      // only mark synced if not edited again meanwhile
      if (fresh && fresh.updated_at === r.updated_at) {
        await putReport({ ...r, _sync: "synced" });
      }
    } catch (err) {
      await putReport({ ...r, _sync: "error" });
      setState({ lastError: "Sync failed — will retry" });
    }
  }
  const left = (await allReports()).filter((r) => r._sync !== "synced").length;
  setState({ syncing: false, pending: left });
}

export function initSync() {
  const online = () => {
    setState({ online: true });
    syncNow();
  };
  const offline = () => setState({ online: false });
  window.addEventListener("online", online);
  window.addEventListener("offline", offline);
  syncNow();
  const iv = setInterval(() => {
    if (navigator.onLine) syncNow();
  }, 30000);
  return () => {
    window.removeEventListener("online", online);
    window.removeEventListener("offline", offline);
    clearInterval(iv);
  };
}

export function deriveStatus(s) {
  if (!s.online) return { label: "Offline", tone: "offline" };
  if (s.syncing) return { label: "Syncing…", tone: "syncing" };
  if (s.lastError && s.pending > 0)
    return { label: "Sync error", tone: "error" };
  if (s.pending > 0) return { label: "Saved locally", tone: "local" };
  return { label: "Synced", tone: "synced" };
}
